import { useContext, useEffect } from "react";
import { newVehicles, vehicles } from "../../../../src/constants/vehicleData";
import { imagesWEBP } from "../../../../src/constants/webpImages";
import { Context } from "../../../../src/context/context";
import { sendAnalytics } from "../../../../src/utils/analyticsCommon";
import dynamic from "next/dynamic";
import Head from "next/head";
import {
  generateProductSchema,
  createOffer,
  createBrand,
  createPropertyValue,
} from "../../../../src/utils/ProductSchemaGenerator.js";
import {
  generateFAQPageSchema,
  convertFAQsToQuestions,
} from "../../../../src/utils/FAQPageSchemaGenerator.js";
import { vehicleFAQs } from "../../../../src/constants/brandFaqs";
const CommercialVehicleLandingPage = dynamic(
  () =>
    import(
      "../../../../src/modules/CommercialVehicleLandingPage/CommercialVehicleLandingPage"
    ),
  { suspense: true }
);
import {
  TITLE,
  META_CONTENT,
  SUB_TEXT,
  MOSTLY_BOUGHT_FOR_HEADING,
  BUSINESS_BENEFIT_HEADING,
  FEATURES_HEADING,
  BRAND_DEALER_HEADING,
  BRAND_DEALER_DESCRIPITION,
  FAQ_HEADING_TEXT,
  VEHICLE_LINK,
  H1_HEADING,
  BREADCRUMB_ITEMS,
  BREADCRUMB_SCHEMA,
} from "../../../../src/constants/electricCommericalVehicle/Altigreen/neEv";

const PRICE_CITIES = [
  "pune",
  "mumbai",
  "bangalore",
  "hyderabad",
  "chennai",
  "kolkata",
  "delhi-ncr",
  "jaipur",
  "lucknow",
  "ahmedabad",
  "indore",
];

const SECONDARY_HEADER_ITEMS = [
  { label: "Altigreen neEV", sectionId: "section-header" },
  { label: "Price", sectionId: "section-price" },
  { label: "Specification", sectionId: "section-specification" },
  { label: "Summary", sectionId: "section-summary" },
];

export default function VehicleModelDetails({
  brand,
  vehicleModel,
  vehicleDataLocal,
  plusIcons,
  latestVehiclesInfo,
  productSchema,
  faqPageSchema,
}) {
  const noentryData = [
    {
      id: 1,
      imageName: imagesWEBP?.businessN0,
    },
    {
      id: 2,
      imageName: imagesWEBP?.businessN1,
    },
    {
      id: 3,
      imageName: imagesWEBP?.businessN2,
    },
    {
      id: 4,
      imageName: imagesWEBP?.businessN3,
    },
  ];

  const useCaseData = [
    {
      id: 1,
      imageName: imagesWEBP?.neevUsecase0,
      title: "Use your Cargo EV across Delhi Without Restrictions",
    },
    {
      id: 2,
      imageName: imagesWEBP?.neevUsecase1,
      title:
        "No 7 AM to 11 AM and 5PM to 9PM Restrictions for ELECTRIC VEHICLES IN Delhi",
    },
    {
      id: 3,
      imageName: imagesWEBP?.neevUsecase2,
      title: "Benefit from Govt. Relaxation for EVs",
    },
    {
      id: 3,
      imageName: imagesWEBP?.neevUsecase3,
      title: "Benefit from Govt. Relaxation for EVs",
    },
  ];
  useEffect(() => {
    sendAnalytics(
      "screen_viewed",
      { screen_name: `electric_commercial_vehicle_${brand}_${vehicleModel}` },
      "en"
    );
  }, [brand, vehicleModel]);

  const state = useContext(Context);
  let location =
    state?.selectedState === "Others" ? "India" : state?.selectedState;

  //We need to add SEO Meta info later
  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productSchema),
          }}
          defer
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqPageSchema),
          }}
          defer
        />
      </Head>
      <CommercialVehicleLandingPage
        title={TITLE}
        metaContent={META_CONTENT}
        vehicleLink={VEHICLE_LINK}
        SUB_TEXT={SUB_TEXT}
        MOSTLY_BOUGHT_FOR_HEADING={MOSTLY_BOUGHT_FOR_HEADING}
        BUSINESS_BENEFIT_HEADING={BUSINESS_BENEFIT_HEADING}
        FEATURES_HEADING={FEATURES_HEADING}
        BRAND_DEALER_HEADING={BRAND_DEALER_HEADING}
        BRAND_DEALER_DESCRIPITION={BRAND_DEALER_DESCRIPITION}
        FAQ_HEADING_TEXT={FAQ_HEADING_TEXT}
        location={location}
        vehicleDataLocal={vehicleDataLocal}
        vehicleName="neev"
        useCaseData={useCaseData}
        noentryData={noentryData}
        latestVehiclesInfo={latestVehiclesInfo}
        H1_HEADING={H1_HEADING}
        BREADCRUMB_ITEMS={BREADCRUMB_ITEMS}
        BREADCRUMB_SCHEMA={BREADCRUMB_SCHEMA}
        secondaryHeaderItems={SECONDARY_HEADER_ITEMS}
        priceCities={PRICE_CITIES}
        pricePathPrefix="Altigreen/neEV"
      />
    </>
  );
}

export const getStaticProps = (context) => {
  let vehicleData = [...vehicles, ...newVehicles];

  const brand = "Altigreen";
  const vehicleModel = "neEV";

  let vehicleDataLocal = vehicleData?.filter(function (vehicle) {
    return vehicle?.vehicle_path_id === `${vehicleModel}`;
  });

  let latestVehiclesInfo = vehicleData?.filter((vehicle) => {
    return vehicle?.vehicle_path_id !== `${vehicleModel}`;
  });

  // Constants are already imported at the top of the file

  // Generate Product Schema
  const vehicleDataItem = vehicleDataLocal?.[0];
  const productImages = vehicleDataItem?.images_287 || [
    imagesWEBP?.neevImage1,
    imagesWEBP?.neevImage2,
    imagesWEBP?.neevImage3,
    imagesWEBP?.neevImage4,
  ].filter(Boolean);

  // Get price range from vehicle data
  const getPriceRange = () => {
    if (!vehicleDataItem?.prices) return { lowPrice: 400000, highPrice: 450000 };
    const allPrices = [];
    Object.values(vehicleDataItem.prices).forEach((statePrices) => {
      Object.values(statePrices).forEach((variant) => {
        if (variant.price) {
          allPrices.push(variant.price * 100000); // Convert lakhs to rupees
        }
      });
    });
    return {
      lowPrice: Math.min(...allPrices),
      highPrice: Math.max(...allPrices),
    };
  };

  const priceRange = getPriceRange();

  const productSchema = generateProductSchema({
    name: "Altigreen neEV",
    description: SUB_TEXT || META_CONTENT[0]?.content,
    image: productImages,
    url: VEHICLE_LINK,
    brand: createBrand({
      name: "Altigreen",
      url: "https://www.turno.club/electric-commercial-vehicles/Altigreen",
    }),
    category: "Electric Commercial Vehicle",
    model: "neEV",
    manufacturer: {
      "@type": "Organization",
      name: "Altigreen Propulsion Labs Private Limited",
    },
    offers: createOffer({
      offerCount: 3,
      lowPrice: priceRange.lowPrice,
      highPrice: priceRange.highPrice,
      priceCurrency: "INR",
      availability: "https://schema.org/InStock",
      url: VEHICLE_LINK,
      seller: {
        "@type": "Organization",
        name: "Turno",
        url: "https://www.turno.club/",
      },
    }),
    additionalProperty: [
      vehicleDataItem?.load
        ? createPropertyValue({ name: "Load Capacity", value: vehicleDataItem.load })
        : null,
      vehicleDataItem?.range
        ? createPropertyValue({ name: "Range", value: vehicleDataItem.range })
        : null,
      vehicleDataItem?.batteryCapacity
        ? createPropertyValue({
            name: "Battery Capacity",
            value: vehicleDataItem.batteryCapacity,
          })
        : null,
      vehicleDataItem?.batteryVoltage
        ? createPropertyValue({
            name: "Battery Voltage",
            value: vehicleDataItem.batteryVoltage,
          })
        : null,
    ].filter(Boolean),
    keywords: META_CONTENT[1]?.content || "Altigreen neEV, electric vehicle",
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    itemCondition: "https://schema.org/NewCondition",
    isFamilyFriendly: true,
    slogan: "Electric cargo vehicle for urban logistics",
  });

  // Generate FAQPage Schema
  const faqQuestions = convertFAQsToQuestions(vehicleFAQs?.neev || [], {
    url: VEHICLE_LINK,
    dateCreated: new Date().toISOString(),
    author: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
    },
  });

  const faqPageSchema = generateFAQPageSchema({
    mainEntity: faqQuestions,
    name: FAQ_HEADING_TEXT || "FAQs on Altigreen neEV",
    description: `Frequently asked questions about Altigreen neEV electric commercial vehicle. ${SUB_TEXT || ""}`,
    url: VEHICLE_LINK,
    headline: FAQ_HEADING_TEXT || "FAQs on Altigreen neEV",
    keywords: META_CONTENT[1]?.content || "Altigreen neEV, FAQ, electric vehicle",
    inLanguage: "en",
    publisher: {
      "@type": "Organization",
      name: "Turno",
      url: "https://www.turno.club/",
      logo: {
        "@type": "ImageObject",
        url: "https://www.turno.club/logoIcon.png",
      },
    },
    datePublished: new Date().toISOString(),
    dateModified: new Date().toISOString(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": VEHICLE_LINK,
    },
    breadcrumb: BREADCRUMB_SCHEMA,
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: productImages[0] || "https://www.turno.club/logoIcon.png",
    },
    isFamilyFriendly: true,
    isAccessibleForFree: true,
    speakable: {
      "@type": "SpeakableSpecification",
      xPath: [
        "/html/head/title",
        "/html/head/meta[@name='description']/@content",
      ],
    },
  });

  return {
    props: { 
      brand, 
      vehicleModel, 
      vehicleDataLocal, 
      latestVehiclesInfo,
      productSchema,
      faqPageSchema,
    },
  };
};
